import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { API_URL } from '../utils/api';
import {
  formatCurrency, formatCurrencyShort, splitCurrency, formatWhen,
} from '../utils/format';
import ProfileMenu from './ProfileMenu';

/**
 * The dashboard. The one dark workspace.
 *
 * Cases closed this month leads, split Pre-litigation / Litigation, against
 * a combined monthly minimum of 45. Money saved sits beside it at a smaller
 * scale. That order is the whole point of the page: the firm's month is
 * measured in cases finished, and the money is what the finishing was worth.
 *
 * On the counts themselves, two rules worth knowing at the call site:
 *
 *   - 45 is a FLOOR across both tracks together. Not 45 each, not an
 *     average, and not a cap — the figure keeps climbing past it.
 *   - A count that could not be computed renders as "—", never as 0. Those
 *     mean opposite things to someone reading the page on the 30th.
 *
 * backend/case_closures.py defines what closed means and why the closures
 * are recorded rather than counted live.
 */

/** A figure that is a real number, or an em dash when the count is missing. */
function Count({ value }) {
  if (value === null || value === undefined) {
    return <span className="lm-unavailable" title="Could not be calculated">—</span>;
  }
  return <>{value}</>;
}

const greetingFor = (hour) => {
  if (hour < 12) return 'Good morning';
  if (hour < 18) return 'Good afternoon';
  return 'Good evening';
};

const STATUS = {
  accepted: { word: 'Accepted', tone: '' },
  counter_offered: { word: 'Counter received', tone: ' lm-warn' },
  sent: { word: 'Sent', tone: ' lm-neutral' },
  pending: { word: 'Sent', tone: ' lm-neutral' },
  rejected: { word: 'Rejected', tone: ' lm-warn' },
};

const Dashboard = ({
  stats, loading, user, onRefresh, refreshing, onNewNegotiation, setActiveTab, onLogout,
}) => {
  // Asked for once, on the one screen everybody opens first.
  const [build, setBuild] = useState('');
  useEffect(() => {
    let alive = true;
    axios.get(`${API_URL}/api/version`, { withCredentials: true })
      .then((r) => { if (alive) setBuild(r.data.build || ''); })
      .catch(() => { /* an unlabelled build is not worth an error */ });
    return () => { alive = false; };
  }, []);

  const today = new Date();
  const dateLabel = today.toLocaleDateString('en-US', {
    weekday: 'long', month: 'long', day: 'numeric', year: 'numeric',
  });
  const firstName = (user?.name || '').trim().split(/\s+/)[0] || 'there';

  if (loading && !stats) {
    return (
      <>
        <div className="lm-top">
          <span>{dateLabel}</span>
          <ProfileMenu user={user} onLogout={onLogout} />
        </div>
        <div className="lm-loading">Loading your month…</div>
      </>
    );
  }

  const s = stats || {};
  const closed = s.closed_this_month || null;
  const thisWeek = s.this_week || { saved: 0, negotiations: 0, delta_pct: null };
  const recent = s.recent_activity || [];
  const followUp = s.needs_followup_count || 0;

  const inScope = s.total_projects ?? 0;
  const settled = (s.cases_final_steps_prelit || 0) + (s.cases_final_steps_lit || 0);
  const activeNegos = s.negotiations_in_negotiation ?? 0;
  const negsPending = s.negotiations_pending ?? 0;
  const totalSavings = s.total_savings || 0;
  const billed = s.total_original_bills_confirmed || 0;
  const savedPct = billed > 0 ? ((totalSavings / billed) * 100).toFixed(1) : null;

  const saved = splitCurrency(thisWeek.saved || 0);
  const delta = thisWeek.delta_pct;
  const monthName = today.toLocaleDateString('en-US', { month: 'long' });

  return (
    <>
      <div className="lm-top">
        <span>{dateLabel}</span>
        <ProfileMenu user={user} onLogout={onLogout} />
      </div>

      <h1>{greetingFor(today.getHours())},<br />{firstName}.</h1>

      <div className="lm-hero lm-outcomes">
        <section className="lm-closures" aria-label={`Cases closed in ${monthName}`}>
          <div className="lm-eyebrow">Cases closed this month</div>
          <div className="lm-closure-split">
            <div>
              <div className="lm-closure-value"><Count value={closed?.prelit} /></div>
              <div className="lm-closure-label">Pre-litigation</div>
            </div>
            <div>
              <div className="lm-closure-value"><Count value={closed?.lit} /></div>
              <div className="lm-closure-label">Litigation</div>
            </div>
          </div>
          <div className="lm-closure-baseline">
            <span><strong><Count value={closed?.total} /></strong> total closed</span>
            <span>Monthly minimum <strong>{closed?.minimum ?? 45}</strong> combined</span>
            {/* Cases Filevine put in a closed phase without telling us when.
                Shown rather than folded into the total — a number nobody can
                date does not belong in a month. */}
            {closed?.undated > 0 && (
              <span title="In a closed phase, but Filevine recorded no date for it">
                <strong>{closed.undated}</strong> undated
              </span>
            )}
          </div>
          <button type="button" className="lm-link" onClick={() => setActiveTab('closeout')}>
            View closeouts →
          </button>
        </section>

        <section className="lm-savings-secondary" aria-label="Money saved this week">
          <div className="lm-eyebrow">Money saved this week</div>
          <div className="lm-money">{saved.dollars}<sup>{saved.cents}</sup></div>
          <div className="lm-savings-comparison">
            {delta !== null && delta !== undefined && (
              <>
                <span className="lm-pill">{delta >= 0 ? '↗' : '↘'}&nbsp; {Math.abs(delta)}%</span>
                <span className="lm-inline-muted">vs. last week</span>
              </>
            )}
          </div>
          <div className="lm-savings-detail">
            {thisWeek.negotiations} negotiation{thisWeek.negotiations === 1 ? '' : 's'}
          </div>
        </section>
      </div>

      <div className="lm-metrics lm-dashboard-metrics">
        <div className="lm-metric">
          <strong>{inScope}</strong>
          <label>Cases in scope</label>
          <small>
            {settled > 0
              ? `${settled} settled · ${Math.max(0, inScope - settled)} active`
              : 'active in-scope cases'}
          </small>
        </div>
        <div className="lm-metric">
          <strong>{activeNegos}</strong>
          <label>Active negotiations</label>
          <small>
            {negsPending > 0 ? `${negsPending} awaiting a provider` : 'none awaiting a provider'}
          </small>
        </div>
        <div className="lm-metric">
          <strong>{formatCurrencyShort(totalSavings)}</strong>
          <label>Saved to date</label>
          <small>{savedPct !== null ? `${savedPct}% of billed amounts` : 'across confirmed reductions'}</small>
        </div>
      </div>

      <section className="lm-panel">
        <div className="lm-rowhead">
          <h2>Recent activity</h2>
          <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
            {/* Refresh is not in the approved mockup, which drew the page
                rather than the product. It pulls the case list from Filevine
                and the team uses it, so it keeps its place beside the
                primary action instead of disappearing with the old header. */}
            <button type="button" className="lm-button" onClick={onRefresh} disabled={refreshing}>
              {refreshing ? 'Refreshing…' : 'Refresh'}
            </button>
            <button type="button" className="lm-button lm-primary"
                    onClick={() => onNewNegotiation && onNewNegotiation()}
                    title="Open a case to start a new negotiation">
              Start negotiation ↗
            </button>
          </div>
        </div>

        <div className="lm-tablewrap">
          <table className="lm-dashboard-activity">
            <thead>
              <tr>
                <th>When</th>
                <th>Provider and case</th>
                <th className="lm-num">Agreement</th>
                <th>Status</th>
              </tr>
            </thead>
            <tbody>
              {recent.length === 0 ? (
                <tr>
                  <td colSpan={4} className="lm-empty">
                    No recent activity yet. New events appear here as your team works.
                  </td>
                </tr>
              ) : recent.map((evt, i) => {
                const when = formatWhen(evt.updated_at);
                const status = STATUS[evt.status] || { word: evt.action || evt.status, tone: ' lm-neutral' };
                const from = evt.initial_amount > 0 && evt.initial_amount !== evt.current_amount;
                return (
                  <tr key={i}>
                    <td>{when.day}<small>{when.time}</small></td>
                    <td>
                      <strong>{evt.provider_name || '—'}</strong>
                      <small>{evt.client_name}</small>
                    </td>
                    <td className="lm-num">
                      {formatCurrency(evt.current_amount)}
                      {from && <small>from {formatCurrency(evt.initial_amount)}</small>}
                    </td>
                    <td><span className={`lm-tag${status.tone}`}>{status.word}</span></td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>

        <div className="lm-bottomline">
          <span>Latest provider responses and agreements</span>
          <button type="button" className="lm-link" onClick={() => setActiveTab('negotiations')}>
            All negotiations →
          </button>
        </div>
      </section>

      {/* Follow-up sits at the foot of the page, not the top of it. */}
      {followUp > 0 && (
        <div className="lm-attention">
          <strong>{followUp}</strong>
          <p>
            Negotiation{followUp === 1 ? '' : 's'} need your attention
            <small>No provider response in more than 7 days</small>
          </p>
          <button type="button" className="lm-link" onClick={() => setActiveTab('negotiations')}>
            Review queue →
          </button>
        </div>
      )}

      <div className="lm-pagefooter">
        <span>Liens department · The May Firm</span>
        {/* Which build is actually running. Twice a reported bug has turned
            out to be code that was never deployed, and establishing that
            cost a round trip each time. It is a page refresh now. */}
        <span>{build ? `Build ${build}` : ''}</span>
        <span>{dateLabel}</span>
      </div>
    </>
  );
};

export default Dashboard;
